import { useState } from "react";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { BUSINESS } from "@/data/site";

const EMPTY = { name: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email";
    if (!form.message.trim()) er.message = "Let us know how we can help";
    return er;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setStatus("loading");
    // Non-functional demo: simulate a send, then show success state.
    setTimeout(() => {
      setStatus("success");
      setForm(EMPTY);
      toast.success("Thanks! We'll be in touch shortly.");
    }, 1100);
  };

  const inputBase =
    "w-full rounded-md border bg-white px-4 py-3 text-brand-ink placeholder:text-muted-foreground/70 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-red/40 focus:border-brand-red";

  return (
    <>
      <SEO title="Contact" description="Get in touch with Chopstick Chinese Corner in Sector xxx, Gurugram. Call, email, get directions or send us a message." />
      <PageHero
        overline="Say hello"
        titleLines={["Let's talk", "food."]}
        subtitle="Questions, group bookings or just want to say hi? Reach us directly, or drop a note below."
      />

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid gap-12 lg:grid-cols-2">
        {/* Info */}
        <Reveal>
          <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { icon: Phone, label: "Call", value: BUSINESS.phoneDisplay, href: `tel:${BUSINESS.phoneHref}`, testid: "contact-phone" },
                { icon: Mail, label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}`, testid: "contact-email" },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <a key={c.label} href={c.href} className="group flex items-start gap-4 border border-brand-ink/12 bg-white p-5 transition-colors hover:border-brand-red" data-testid={c.testid}>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-red/10 text-brand-red group-hover:bg-brand-red group-hover:text-white transition-colors">
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-xs uppercase tracking-[0.16em] text-muted-foreground">{c.label}</span>
                      <span className="block text-brand-ink font-medium break-words">{c.value}</span>
                    </span>
                  </a>
                );
              })}
            </div>

            <div className="border border-brand-ink/12 bg-white p-6">
              <div className="flex items-start gap-3">
                <MapPin className="text-brand-red mt-0.5 shrink-0" size={20} />
                <address className="not-italic text-brand-ink leading-relaxed">
                  {BUSINESS.addressLine1}<br />
                  {BUSINESS.addressLine2}, {BUSINESS.addressRegion}
                </address>
              </div>
              <div className="mt-5 pt-5 border-t border-brand-ink/10">
                <div className="flex items-center gap-2 mb-3 text-brand-ink font-medium">
                  <Clock size={18} className="text-brand-red" /> Opening hours
                </div>
                <ul className="space-y-1.5 text-sm">
                  {BUSINESS.hours.map((h) => (
                    <li key={h.day} className="flex justify-between gap-4 text-muted-foreground">
                      <span>{h.day}</span>
                      <span className="text-brand-ink">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <CTAButton href={BUSINESS.directionsUrl} variant="ghost" className="mt-6 w-full" target="_blank" rel="noopener noreferrer">
                Get directions
              </CTAButton>
            </div>

            <div className="overflow-hidden border border-brand-ink/12">
              <iframe
                title="Map to Chopstick Chinese Corner, Sector xxx Gurugram"
                src={BUSINESS.mapEmbed}
                className="w-full h-[300px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <div className="border border-brand-ink/12 bg-white p-7 md:p-9">
            {status === "success" ? (
              <div className="flex flex-col items-center text-center py-12" data-testid="contact-success">
                <CheckCircle2 className="text-brand-red" size={48} />
                <h2 className="mt-5 font-display text-3xl text-brand-ink">Message sent</h2>
                <p className="mt-3 text-muted-foreground max-w-sm">
                  Thanks for reaching out. This is a demo form, so for anything
                  urgent please call us directly.
                </p>
                <CTAButton onClick={() => setStatus("idle")} variant="ghost" className="mt-7" icon={false}>
                  Send another message
                </CTAButton>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                <h2 className="font-display text-3xl text-brand-ink">Send a message</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Note: this is a demo form and does not send email yet.
                </p>

                <div className="mt-7 space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-brand-ink mb-1.5">Name</label>
                    <input id="name" type="text" value={form.name} onChange={update("name")} placeholder="Your name"
                      className={`${inputBase} ${errors.name ? "border-destructive" : "border-brand-ink/15"}`}
                      data-testid="contact-input-name" aria-invalid={!!errors.name} />
                    {errors.name && <p className="mt-1 text-sm text-destructive" data-testid="error-name">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-brand-ink mb-1.5">Email</label>
                    <input id="email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com"
                      className={`${inputBase} ${errors.email ? "border-destructive" : "border-brand-ink/15"}`}
                      data-testid="contact-input-email" aria-invalid={!!errors.email} />
                    {errors.email && <p className="mt-1 text-sm text-destructive" data-testid="error-email">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-brand-ink mb-1.5">Message</label>
                    <textarea id="message" rows={5} value={form.message} onChange={update("message")} placeholder="How can we help?"
                      className={`${inputBase} resize-none ${errors.message ? "border-destructive" : "border-brand-ink/15"}`}
                      data-testid="contact-input-message" aria-invalid={!!errors.message} />
                    {errors.message && <p className="mt-1 text-sm text-destructive" data-testid="error-message">{errors.message}</p>}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-red px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-brand-red-dark disabled:opacity-70"
                  data-testid="contact-submit"
                >
                  {status === "loading" ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending…</>
                  ) : (
                    <><Send size={16} /> Send message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </>
  );
}
